import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock3,
  Flag,
  Loader2,
  Send,
  Sparkles,
  ChevronDown,
  LayoutGrid,
  Eye,
  ShieldCheck,
  Timer,
  Hash,
  XCircle,
  HelpCircle,
  RotateCcw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { jambCbtService } from '../../services/jamb/jambCbt.service';
import type {
  JambQuestion,
  JambSubject,
  JambSubmitResult,
  JambReviewQuestion,
} from '../../services/jamb/jambCbt.service';

const TEST_MINUTES = 45;
const QUESTION_BATCH = 40;

const JambCbtTest: React.FC = () => {
  const { subjectId } = useParams<{ subjectId: string }>();
  const navigate = useNavigate();

  const [subject, setSubject] = useState<JambSubject | null>(null);
  const [questions, setQuestions] = useState<JambQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [flagged, setFlagged] = useState<Set<string>>(new Set());

  const [attemptId, setAttemptId] = useState<string | null>(null);

  const [current, setCurrent] = useState(0);
  const [reviewCurrent, setReviewCurrent] = useState(0);

  const [secondsLeft, setSecondsLeft] = useState(TEST_MINUTES * 60);
  const [startedAt, setStartedAt] = useState<number>(Date.now());

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [result, setResult] = useState<JambSubmitResult | null>(null);

  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showQuestionNavigator, setShowQuestionNavigator] = useState(true);
  const [showReviewNavigator, setShowReviewNavigator] = useState(true);

  /* ------------------------------------------------------------------ */
  /* Derived values                                                     */
  /* ------------------------------------------------------------------ */

  const answeredCount = Object.keys(answers).length;

  const currentQuestion = useMemo(() => {
    if (!questions.length) return null;

    const safeIndex = Math.max(
      0,
      Math.min(current, questions.length - 1),
    );

    return questions[safeIndex] || null;
  }, [questions, current]);

  const reviewQuestions = useMemo<JambReviewQuestion[]>(() => {
    return result?.reviewQuestions || [];
  }, [result]);

  const currentReviewQuestion = useMemo(() => {
    if (!reviewQuestions.length) return null;

    const safeIndex = Math.max(
      0,
      Math.min(reviewCurrent, reviewQuestions.length - 1),
    );

    return reviewQuestions[safeIndex] || null;
  }, [reviewQuestions, reviewCurrent]);

  const unansweredCount = Math.max(
    0,
    questions.length - answeredCount,
  );

  const formatTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(
      seconds,
    ).padStart(2, '0')}`;
  };

  const getOptionText = (
    question: JambQuestion | JambReviewQuestion,
    option: string | null | undefined,
  ) => {
    if (!option) return '';

    switch (option.toUpperCase()) {
      case 'A':
        return question.option_a;
      case 'B':
        return question.option_b;
      case 'C':
        return question.option_c;
      case 'D':
        return question.option_d;
      default:
        return '';
    }
  };

  const optionLabels = ['A', 'B', 'C', 'D'];

  /* ------------------------------------------------------------------ */
  /* Load CBT                                                           */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    let mounted = true;

    const loadTest = async () => {
      try {
        setLoading(true);

        if (!subjectId) {
          throw new Error('No JAMB subject was specified.');
        }

        const subjects = await jambCbtService.getSubjects();

        if (!mounted) return;

        const selectedSubject =
          subjects.find((item) => item.id === subjectId) || null;

        if (!selectedSubject) {
          throw new Error('The selected JAMB subject could not be found.');
        }

        setSubject(selectedSubject);

        const student = await jambCbtService.getStudent();

        if (!mounted) return;

        const registration =
          await jambCbtService.getRegistration(student.id);

        if (!registration) {
          throw new Error(
            'You do not have an active JAMB registration.',
          );
        }

        const registeredSubject =
          registration.jamb_registration_subjects?.find(
            (item) => item.subject_id === subjectId,
          );

        if (!registeredSubject) {
          throw new Error(
            'This subject is not included in your JAMB registration.',
          );
        }

        const cooldown = await jambCbtService.getCooldownInfo(
          registration.id,
          subjectId,
        );

        if (!cooldown.canStart) {
          const remainingMs = Math.max(
            0,
            cooldown.nextAvailableAt
              ? cooldown.nextAvailableAt.getTime() - Date.now()
              : 0,
          );

          const remainingHours = Math.ceil(
            remainingMs / (60 * 60 * 1000),
          );

          throw new Error(
            `You cannot retake ${selectedSubject.name} yet. Please wait approximately ${remainingHours} hour${
              remainingHours === 1 ? '' : 's'
            }.`,
          );
        }

        const usedQuestionIds =
          await jambCbtService.getUsedQuestionIds(
            registration.id,
            subjectId,
          );

        if (!mounted) return;

        let loadedQuestions =
          await jambCbtService.getUnseenQuestions(
            subjectId,
            usedQuestionIds,
            QUESTION_BATCH,
          );

        /*
         * If there are not enough unseen questions, reset the pool and
         * start using the full active question pool again.
         */
        if (loadedQuestions.length < QUESTION_BATCH) {
          const total =
            await jambCbtService.getTotalActiveQuestions(subjectId);

          if (total >= QUESTION_BATCH) {
            await jambCbtService.resetQuestionPool(
              registration.id,
              subjectId,
            );

            loadedQuestions =
              await jambCbtService.getUnseenQuestions(
                subjectId,
                [],
                QUESTION_BATCH,
              );
          }
        }

        if (!mounted) return;

        /*
         * Safety guard:
         * make absolutely sure the client state contains one entry per
         * unique question ID.
         */
        const uniqueQuestions: JambQuestion[] = [];
        const seenIds = new Set<string>();

        for (const question of loadedQuestions) {
          if (!seenIds.has(question.id)) {
            seenIds.add(question.id);
            uniqueQuestions.push(question);
          }
        }

        if (!uniqueQuestions.length) {
          throw new Error(
            'No active questions are available for this subject.',
          );
        }

        setQuestions(uniqueQuestions);

        const attempt =
          await jambCbtService.startAttempt(
            registration.id,
            subjectId,
            uniqueQuestions.map((question) => question.id),
          );

        if (!mounted) return;

        setAttemptId(attempt.id);

        await jambCbtService.setAttemptQuestionCount(
          attempt.id,
          uniqueQuestions.length,
        );

        if (!mounted) return;

        setStartedAt(Date.now());
        setSecondsLeft(TEST_MINUTES * 60);
        setCurrent(0);
        setAnswers({});
        setFlagged(new Set());
      } catch (error: any) {
        if (!mounted) return;

        toast.error(
          error?.message ||
            'Unable to load the JAMB CBT.',
          {
            duration: 6000,
          },
        );

        navigate('/jamb-cbt');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadTest();

    return () => {
      mounted = false;
    };
  }, [subjectId, navigate]);

  /* ------------------------------------------------------------------ */
  /* Timer                                                              */
  /* ------------------------------------------------------------------ */

  const submit = async () => {
    if (!attemptId || submitting || result) return;

    try {
      setSubmitting(true);

      const duration = Math.max(
        0,
        Math.floor((Date.now() - startedAt) / 1000),
      );

      const data = await jambCbtService.submitAttempt(
        attemptId,
        answers,
        duration,
      );

      setResult(data);
      setReviewCurrent(0);
      setShowSubmitConfirm(false);
    } catch (error: any) {
      toast.error(
        error?.message || 'Unable to submit CBT.',
        {
          duration: 5000,
        },
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (
      loading ||
      result ||
      !questions.length ||
      !attemptId
    ) {
      return;
    }

    if (secondsLeft <= 0) {
      toast.error(
        'Time is up. Your CBT is being submitted automatically.',
        {
          duration: 4000,
        },
      );

      submit();
      return;
    }

    const timer = window.setInterval(() => {
      setSecondsLeft((previous) =>
        Math.max(0, previous - 1),
      );
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [
    loading,
    result,
    questions.length,
    attemptId,
    secondsLeft,
  ]);

  /* ------------------------------------------------------------------ */
  /* Navigation                                                         */
  /* ------------------------------------------------------------------ */

  const goToQuestion = (index: number) => {
    if (!questions.length) return;

    const safeIndex = Math.max(
      0,
      Math.min(index, questions.length - 1),
    );

    setCurrent(safeIndex);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const goToReviewQuestion = (index: number) => {
    if (!reviewQuestions.length) return;

    const safeIndex = Math.max(
      0,
      Math.min(index, reviewQuestions.length - 1),
    );

    setReviewCurrent(safeIndex);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const selectAnswer = (option: string) => {
    if (!currentQuestion || result) return;

    setAnswers((previous) => ({
      ...previous,
      [currentQuestion.id]: option,
    }));
  };

  const toggleFlag = () => {
    if (!currentQuestion || result) return;

    setFlagged((previous) => {
      const next = new Set(previous);

      if (next.has(currentQuestion.id)) {
        next.delete(currentQuestion.id);
      } else {
        next.add(currentQuestion.id);
      }

      return next;
    });
  };

  const previousQuestion = () => {
    setCurrent((previous) =>
      Math.max(previous - 1, 0),
    );
  };

  const nextQuestion = () => {
    setCurrent((previous) =>
      Math.min(
        previous + 1,
        Math.max(questions.length - 1, 0),
      ),
    );
  };

  /* ------------------------------------------------------------------ */
  /* Loading                                                            */
  /* ------------------------------------------------------------------ */

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-xl p-8 text-center">
          <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
            <Loader2 className="w-7 h-7 text-blue-600 animate-spin" />
          </div>

          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Preparing your JAMB CBT
          </h2>

          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Selecting your questions and preparing the examination...
          </p>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Submission review                                                 */
  /* ------------------------------------------------------------------ */

  if (result) {
    const correct = Number(result.correct || 0);
    const wrong = Number(result.wrong || 0);
    const unanswered = Number(result.unanswered || 0);
    const total = Number(
      result.question_count || reviewQuestions.length || questions.length,
    );

    const score = Number(result.score || 0);

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Result header */}
          <div className="rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600 px-6 sm:px-8 py-8 text-white">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2 text-blue-100 text-sm font-medium mb-2">
                    <CheckCircle2 className="w-5 h-5" />
                    CBT Submitted Successfully
                  </div>

                  <h1 className="text-2xl sm:text-3xl font-bold">
                    {subject?.name || 'JAMB CBT'} Review
                  </h1>

                  <p className="mt-2 text-blue-100 text-sm">
                    Review every question, your selected answer,
                    and the correct answer.
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-white/10 border border-white/20 px-5 py-4 text-center min-w-[130px]">
                    <div className="text-xs uppercase tracking-wider text-blue-100">
                      Score
                    </div>
                    <div className="text-4xl font-extrabold mt-1">
                      {score}%
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-gray-200 dark:divide-gray-800">
              <div className="p-5 text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {total}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Total Questions
                </div>
              </div>

              <div className="p-5 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {correct}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Correct
                </div>
              </div>

              <div className="p-5 text-center">
                <div className="text-2xl font-bold text-red-600">
                  {wrong}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Wrong
                </div>
              </div>

              <div className="p-5 text-center">
                <div className="text-2xl font-bold text-gray-500">
                  {unanswered}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Unanswered
                </div>
              </div>
            </div>
          </div>

          {/* Review */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-6">
            {/* Review navigator */}
            <aside className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm p-5 h-fit lg:sticky lg:top-6">
              <button
                type="button"
                onClick={() =>
                  setShowReviewNavigator(
                    (previous) => !previous,
                  )
                }
                className="w-full flex items-center justify-between mb-4"
              >
                <div className="flex items-center gap-2">
                  <LayoutGrid className="w-5 h-5 text-blue-600" />
                  <span className="font-bold text-gray-900 dark:text-white">
                    Question Review
                  </span>
                </div>

                <ChevronDown
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    showReviewNavigator
                      ? 'rotate-180'
                      : ''
                  }`}
                />
              </button>

              {showReviewNavigator && (
                <>
                  <div className="grid grid-cols-5 gap-2 max-h-[360px] overflow-y-auto pr-1">
                    {reviewQuestions.map(
                      (question, index) => {
                        const isCurrent =
                          reviewCurrent === index;

                        let stateClass =
                          'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';

                        if (question.isCorrect) {
                          stateClass =
                            'bg-green-100 text-green-700 border-green-300 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800';
                        } else if (
                          question.selectedOption
                        ) {
                          stateClass =
                            'bg-red-100 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800';
                        }

                        if (isCurrent) {
                          stateClass +=
                            ' ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-gray-900';
                        }

                        return (
                          <button
                            key={`${question.id}-${index}`}
                            type="button"
                            onClick={() =>
                              goToReviewQuestion(index)
                            }
                            className={`relative h-10 rounded-lg border text-sm font-bold transition-all hover:scale-[1.03] ${stateClass}`}
                            aria-label={`Review question ${
                              index + 1
                            }`}
                          >
                            {index + 1}

                            {question.isCorrect && (
                              <CheckCircle2 className="absolute -top-1.5 -right-1.5 w-4 h-4 text-green-600 bg-white dark:bg-gray-900 rounded-full" />
                            )}

                            {!question.isCorrect &&
                              question.selectedOption && (
                                <XCircle className="absolute -top-1.5 -right-1.5 w-4 h-4 text-red-600 bg-white dark:bg-gray-900 rounded-full" />
                              )}
                          </button>
                        );
                      },
                    )}
                  </div>

                  <div className="mt-5 space-y-2 text-xs">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <span className="w-3 h-3 rounded bg-green-500" />
                      Correct
                    </div>

                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <span className="w-3 h-3 rounded bg-red-500" />
                      Wrong
                    </div>

                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <span className="w-3 h-3 rounded bg-gray-400" />
                      Unanswered
                    </div>
                  </div>
                </>
              )}

              <div className="mt-5 pt-5 border-t border-gray-200 dark:border-gray-800">
                <div className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <ShieldCheck className="w-4 h-4 shrink-0 text-blue-500" />
                  <span>
                    This review is read-only. Your submitted answers
                    cannot be changed.
                  </span>
                </div>
              </div>
            </aside>

            {/* Current review question */}
            <main>
              {currentReviewQuestion ? (
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm overflow-hidden">
                  {/* Question header */}
                  <div className="px-5 sm:px-7 py-5 border-b border-gray-200 dark:border-gray-800">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            currentReviewQuestion.isCorrect
                              ? 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300'
                              : currentReviewQuestion.selectedOption
                              ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                          }`}
                        >
                          {currentReviewQuestion.isCorrect ? (
                            <CheckCircle2 className="w-5 h-5" />
                          ) : currentReviewQuestion.selectedOption ? (
                            <XCircle className="w-5 h-5" />
                          ) : (
                            <HelpCircle className="w-5 h-5" />
                          )}
                        </div>

                        <div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Question {reviewCurrent + 1} of{' '}
                            {reviewQuestions.length}
                          </div>

                          <div className="font-bold text-gray-900 dark:text-white">
                            {currentReviewQuestion.isCorrect
                              ? 'Correct Answer'
                              : currentReviewQuestion.selectedOption
                              ? 'Incorrect Answer'
                              : 'Not Answered'}
                          </div>
                        </div>
                      </div>

                      {currentReviewQuestion.topic && (
                        <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                          {currentReviewQuestion.topic}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Question */}
                  <div className="p-5 sm:p-7">
                    <div className="flex gap-4">
                      <div className="shrink-0 text-sm font-bold text-blue-600">
                        {reviewCurrent + 1}.
                      </div>

                      <div className="flex-1">
                        <div className="text-base sm:text-lg leading-8 font-medium text-gray-900 dark:text-white whitespace-pre-wrap">
                          {currentReviewQuestion.question_text}
                        </div>

                        <div className="mt-6 space-y-3">
                          {optionLabels.map((option) => {
                            const isSelected =
                              currentReviewQuestion.selectedOption?.toUpperCase() ===
                              option;

                            const isCorrect =
                              currentReviewQuestion.correctOption?.toUpperCase() ===
                              option;

                            let optionClass =
                              'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 text-gray-800 dark:text-gray-200';

                            if (isCorrect) {
                              optionClass =
                                'border-green-400 bg-green-50 dark:border-green-700 dark:bg-green-950/30 text-green-900 dark:text-green-100';
                            } else if (isSelected) {
                              optionClass =
                                'border-red-400 bg-red-50 dark:border-red-700 dark:bg-red-950/30 text-red-900 dark:text-red-100';
                            }

                            return (
                              <div
                                key={option}
                                className={`relative rounded-xl border-2 px-4 py-4 ${optionClass}`}
                              >
                                <div className="flex items-start gap-3">
                                  <div
                                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-sm ${
                                      isCorrect
                                        ? 'bg-green-600 text-white'
                                        : isSelected
                                        ? 'bg-red-600 text-white'
                                        : 'bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600'
                                    }`}
                                  >
                                    {option}
                                  </div>

                                  <div className="flex-1 pt-1 text-sm sm:text-base">
                                    {getOptionText(
                                      currentReviewQuestion,
                                      option,
                                    )}
                                  </div>

                                  <div className="shrink-0 flex flex-col items-end gap-1">
                                    {isCorrect && (
                                      <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 dark:text-green-300">
                                        <CheckCircle2 className="w-4 h-4" />
                                        Correct
                                      </span>
                                    )}

                                    {isSelected &&
                                      !isCorrect && (
                                        <span className="inline-flex items-center gap-1 text-xs font-bold text-red-700 dark:text-red-300">
                                          <XCircle className="w-4 h-4" />
                                          Your answer
                                        </span>
                                      )}

                                    {isSelected &&
                                      isCorrect && (
                                        <span className="text-xs font-bold text-green-700 dark:text-green-300">
                                          Your answer
                                        </span>
                                      )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Answer summary */}
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 p-4">
                            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              Your Answer
                            </div>

                            <div className="mt-1 font-bold text-gray-900 dark:text-white">
                              {currentReviewQuestion.selectedOption
                                ? `${currentReviewQuestion.selectedOption}. ${getOptionText(
                                    currentReviewQuestion,
                                    currentReviewQuestion.selectedOption,
                                  )}`
                                : 'Not answered'}
                            </div>
                          </div>

                          <div className="rounded-xl bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900/50 p-4">
                            <div className="text-xs uppercase tracking-wide text-green-600 dark:text-green-400">
                              Correct Answer
                            </div>

                            <div className="mt-1 font-bold text-green-900 dark:text-green-100">
                              {currentReviewQuestion.correctOption
                                ? `${currentReviewQuestion.correctOption}. ${getOptionText(
                                    currentReviewQuestion,
                                    currentReviewQuestion.correctOption,
                                  )}`
                                : 'Unavailable'}
                            </div>
                          </div>
                        </div>

                        {/* Explanation */}
                        {currentReviewQuestion.explanation && (
                          <div className="mt-6 rounded-xl border border-blue-200 dark:border-blue-900/50 bg-blue-50 dark:bg-blue-950/20 p-5">
                            <div className="flex items-center gap-2 font-bold text-blue-800 dark:text-blue-200">
                              <Sparkles className="w-5 h-5" />
                              Explanation
                            </div>

                            <p className="mt-2 text-sm leading-7 text-blue-900 dark:text-blue-100 whitespace-pre-wrap">
                              {currentReviewQuestion.explanation}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Review navigation */}
                  <div className="px-5 sm:px-7 py-5 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3">
                    <button
                      type="button"
                      disabled={reviewCurrent <= 0}
                      onClick={() =>
                        goToReviewQuestion(
                          reviewCurrent - 1,
                        )
                      }
                      className="inline-flex items-center gap-2 rounded-xl border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Previous
                    </button>

                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {reviewCurrent + 1} /{' '}
                      {reviewQuestions.length}
                    </span>

                    <button
                      type="button"
                      disabled={
                        reviewCurrent >=
                        reviewQuestions.length - 1
                      }
                      onClick={() =>
                        goToReviewQuestion(
                          reviewCurrent + 1,
                        )
                      }
                      className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-blue-700"
                    >
                      Next
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-10 text-center">
                  <AlertTriangle className="w-10 h-10 mx-auto text-amber-500" />
                  <h2 className="mt-4 text-lg font-bold text-gray-900 dark:text-white">
                    Review data unavailable
                  </h2>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Your score was submitted, but the detailed question
                    review could not be loaded.
                  </p>
                </div>
              )}
            </main>
          </div>

          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={() => navigate('/jamb-cbt')}
              className="inline-flex items-center gap-2 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-6 py-3 font-semibold hover:opacity-90"
            >
              <ArrowLeft className="w-4 h-4" />
              Return to JAMB CBT
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* No current question                                                */
  /* ------------------------------------------------------------------ */

  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-xl p-8 text-center">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-500" />

          <h2 className="mt-4 text-xl font-bold text-gray-900 dark:text-white">
            No question available
          </h2>

          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            There is no question available for this CBT.
          </p>

          <button
            type="button"
            onClick={() => navigate('/jamb-cbt')}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-blue-600 text-white px-5 py-3 font-semibold hover:bg-blue-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to JAMB CBT
          </button>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Active CBT                                                         */
  /* ------------------------------------------------------------------ */

  const selectedAnswer =
    answers[currentQuestion.id] || null;

  const progressPercentage =
    questions.length > 0
      ? ((current + 1) / questions.length) * 100
      : 0;

  const isLastQuestion =
    current === questions.length - 1;

  const timerDanger = secondsLeft <= 5 * 60;
  const timerWarning =
    secondsLeft > 5 * 60 && secondsLeft <= 10 * 60;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-blue-600 text-white flex items-center justify-center">
                  <Sparkles className="w-5 h-5" />
                </div>

                <div className="min-w-0">
                  <div className="font-bold text-gray-900 dark:text-white truncate">
                    {subject?.name || 'JAMB CBT'}
                  </div>

                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Question {current + 1} of {questions.length}
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 font-bold text-sm ${
                timerDanger
                  ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300 animate-pulse'
                  : timerWarning
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                  : 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
              }`}
            >
              <Timer className="w-5 h-5" />
              {formatTime(secondsLeft)}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Exam status */}
        <div className="mb-6 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-950/40 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-green-600" />
              </div>

              <div>
                <div className="font-bold text-gray-900 dark:text-white">
                  JAMB CBT Examination
                </div>

                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Select an answer for each question. You may navigate
                  between questions before submitting.
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 px-3 py-1.5 font-semibold">
                <Hash className="w-3.5 h-3.5" />
                {questions.length} Questions
              </span>

              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300 px-3 py-1.5 font-semibold">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {answeredCount} Answered
              </span>

              <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-3 py-1.5 font-semibold">
                <HelpCircle className="w-3.5 h-3.5" />
                {unansweredCount} Unanswered
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-6">
          {/* Question navigator */}
          <aside className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm p-5 h-fit lg:sticky lg:top-24">
            <button
              type="button"
              onClick={() =>
                setShowQuestionNavigator(
                  (previous) => !previous,
                )
              }
              className="w-full flex items-center justify-between mb-4"
            >
              <div className="flex items-center gap-2">
                <LayoutGrid className="w-5 h-5 text-blue-600" />

                <span className="font-bold text-gray-900 dark:text-white">
                  Questions
                </span>
              </div>

              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform ${
                  showQuestionNavigator
                    ? 'rotate-180'
                    : ''
                }`}
              />
            </button>

            {showQuestionNavigator && (
              <>
                <div className="grid grid-cols-5 gap-2 max-h-[330px] overflow-y-auto pr-1">
                  {questions.map((question, index) => {
                    const isCurrent = current === index;
                    const isAnswered = Boolean(
                      answers[question.id],
                    );
                    const isFlagged = flagged.has(
                      question.id,
                    );

                    let buttonClass =
                      'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';

                    if (isAnswered) {
                      buttonClass =
                        'bg-green-100 text-green-700 border-green-300 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800';
                    }

                    if (isCurrent) {
                      buttonClass =
                        'bg-blue-600 text-white border-blue-600 shadow-md ring-2 ring-blue-300 dark:ring-blue-900';
                    }

                    return (
                      <button
                        key={`${question.id}-${index}`}
                        type="button"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          goToQuestion(index);
                        }}
                        className={`relative h-10 rounded-lg border text-sm font-bold transition-all hover:scale-[1.03] ${buttonClass}`}
                        aria-label={`Go to question ${
                          index + 1
                        }`}
                        aria-current={
                          isCurrent
                            ? 'step'
                            : undefined
                        }
                      >
                        {index + 1}

                        {isFlagged && (
                          <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-amber-500 border-2 border-white dark:border-gray-900" />
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-5 space-y-2 text-xs">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <span className="w-3 h-3 rounded bg-blue-600" />
                    Current
                  </div>

                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <span className="w-3 h-3 rounded bg-green-500" />
                    Answered
                  </div>

                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <span className="w-3 h-3 rounded bg-gray-400" />
                    Unanswered
                  </div>

                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <span className="w-3 h-3 rounded bg-amber-500" />
                    Flagged
                  </div>
                </div>
              </>
            )}

            {/* Progress */}
            <div className="mt-5 pt-5 border-t border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="font-medium text-gray-500 dark:text-gray-400">
                  Progress
                </span>

                <span className="font-bold text-gray-900 dark:text-white">
                  {answeredCount}/{questions.length}
                </span>
              </div>

              <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                <div
                  className="h-full bg-blue-600 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(
                      100,
                      (answeredCount /
                        Math.max(questions.length, 1)) *
                        100,
                    )}%`,
                  }}
                />
              </div>
            </div>
          </aside>

          {/* Question area */}
          <section>
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm overflow-hidden">
              {/* Question header */}
              <div className="px-5 sm:px-7 py-5 border-b border-gray-200 dark:border-gray-800">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-blue-600">
                      <span>
                        Question {current + 1}
                      </span>

                      <span className="text-gray-300 dark:text-gray-700">
                        /
                      </span>

                      <span className="text-gray-500 dark:text-gray-400">
                        {questions.length}
                      </span>
                    </div>

                    {currentQuestion.topic && (
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {currentQuestion.topic}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={toggleFlag}
                    className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold border transition ${
                      flagged.has(currentQuestion.id)
                        ? 'bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300'
                        : 'bg-white border-gray-300 text-gray-600 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <Flag className="w-4 h-4" />
                    {flagged.has(currentQuestion.id)
                      ? 'Flagged'
                      : 'Flag Question'}
                  </button>
                </div>
              </div>

              {/* Question content */}
              <div className="p-5 sm:p-8">
                <div className="flex gap-4">
                  <div className="shrink-0 hidden sm:flex w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 items-center justify-center font-bold">
                    {current + 1}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg sm:text-xl font-semibold leading-8 text-gray-900 dark:text-white whitespace-pre-wrap">
                      {currentQuestion.question_text}
                    </h2>

                    {/* Options */}
                    <div className="mt-8 space-y-3">
                      {optionLabels.map((option) => {
                        const isSelected =
                          selectedAnswer === option;

                        const optionText =
                          getOptionText(
                            currentQuestion,
                            option,
                          );

                        return (
                          <button
                            key={option}
                            type="button"
                            onClick={() =>
                              selectAnswer(option)
                            }
                            className={`w-full text-left rounded-xl border-2 px-4 py-4 transition-all ${
                              isSelected
                                ? 'border-blue-600 bg-blue-50 dark:border-blue-500 dark:bg-blue-950/30 shadow-sm'
                                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-gray-50 dark:hover:bg-gray-800/60'
                            }`}
                          >
                            <div className="flex items-start gap-4">
                              <div
                                className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-bold text-sm ${
                                  isSelected
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                                }`}
                              >
                                {option}
                              </div>

                              <div
                                className={`pt-1 flex-1 text-sm sm:text-base leading-6 ${
                                  isSelected
                                    ? 'text-blue-900 dark:text-blue-100 font-medium'
                                    : 'text-gray-800 dark:text-gray-200'
                                }`}
                              >
                                {optionText}
                              </div>

                              {isSelected && (
                                <CheckCircle2 className="w-5 h-5 text-blue-600 shrink-0 mt-1" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom navigation */}
              <div className="px-5 sm:px-7 py-5 border-t border-gray-200 dark:border-gray-800">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                  <button
                    type="button"
                    disabled={current <= 0}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      previousQuestion();
                    }}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 dark:border-gray-700 px-5 py-3 text-sm font-semibold text-gray-700 dark:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Previous
                  </button>

                  <div className="text-center text-sm font-medium text-gray-500 dark:text-gray-400">
                    {answeredCount} of {questions.length} answered
                  </div>

                  {isLastQuestion ? (
                    <button
                      type="button"
                      onClick={() =>
                        setShowSubmitConfirm(true)
                      }
                      disabled={submitting}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 text-white px-5 py-3 text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
                    >
                      <Send className="w-4 h-4" />
                      Submit CBT
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        nextQuestion();
                      }}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 text-white px-5 py-3 text-sm font-semibold hover:bg-blue-700"
                    >
                      Next
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile quick navigator */}
            <div className="mt-4 lg:hidden bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-white">
                  <Eye className="w-4 h-4 text-blue-600" />
                  Question Navigator
                </div>

                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {current + 1}/{questions.length}
                </span>
              </div>

              <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                {questions.map((question, index) => {
                  const isCurrent = current === index;
                  const isAnswered = Boolean(
                    answers[question.id],
                  );

                  return (
                    <button
                      key={`mobile-${question.id}-${index}`}
                      type="button"
                      onClick={() =>
                        goToQuestion(index)
                      }
                      className={`shrink-0 w-10 h-10 rounded-lg border text-sm font-bold ${
                        isCurrent
                          ? 'bg-blue-600 text-white border-blue-600'
                          : isAnswered
                          ? 'bg-green-100 text-green-700 border-green-300 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800'
                          : 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                      }`}
                    >
                      {index + 1}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Submit confirmation modal */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-950/40 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>

              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Submit your CBT?
                </h2>

                <p className="mt-2 text-sm leading-6 text-gray-500 dark:text-gray-400">
                  You have answered{' '}
                  <strong className="text-gray-900 dark:text-white">
                    {answeredCount}
                  </strong>{' '}
                  of{' '}
                  <strong className="text-gray-900 dark:text-white">
                    {questions.length}
                  </strong>{' '}
                  questions.
                </p>

                {unansweredCount > 0 && (
                  <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                    {unansweredCount} question
                    {unansweredCount === 1
                      ? ''
                      : 's'} will remain unanswered.
                  </p>
                )}
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() =>
                  setShowSubmitConfirm(false)
                }
                disabled={submitting}
                className="flex-1 rounded-xl border border-gray-300 dark:border-gray-700 px-4 py-3 font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                Continue Test
              </button>

              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 text-white px-4 py-3 font-semibold hover:bg-green-700 disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit Now
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-submit overlay */}
      {submitting && !showSubmitConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-2xl p-8 text-center">
            <Loader2 className="w-10 h-10 mx-auto text-blue-600 animate-spin" />

            <h2 className="mt-4 text-lg font-bold text-gray-900 dark:text-white">
              Submitting your CBT
            </h2>

            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Please wait while your answers are being marked.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default JambCbtTest;